import "./flyout-list-item.css";

import type { ListViewItemProps } from "@tql/ui-components-react/kendo/listview";
import { ListViewItemWrapper } from "@tql/ui-components-react/kendo/listview";
import { Checkbox } from "@tql/ui-components-react/tql/inputs/beta";
import { memo } from "react";

import type { DefaultDocument } from "~/shared/types/payment-types";

type FlyoutListItemProps = {
	item: DefaultDocument;
	isSelected: boolean;
	isChecked: boolean;
	onItemClick: (item: DefaultDocument) => void;
	onCheckboxChange: (item: DefaultDocument) => void;
};

export const FlyoutListItem = memo(
	({
		item,
		isSelected,
		isChecked,
		onItemClick,
		onCheckboxChange,
	}: FlyoutListItemProps) => {
		const handleClick = () => {
			onItemClick(item);
		};

		const handleKeyDown = (e: React.KeyboardEvent) => {
			if (e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				onItemClick(item);
			}
		};

		return (
			<div
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				role={"button"}
				style={{ cursor: "pointer" }}
				tabIndex={0}
			>
				<ListViewItemWrapper
					className={`flyout-list-wrapper ${isSelected ? "selected-doc" : ""}`}
				>
					<div
						className={"flyout-item-element"}
						onClick={(e) => e.stopPropagation()} // Stop propagation so handleClick doesn't fire twice
						onKeyDown={(e) => e.key === "Enter" && e.stopPropagation()}
						role={"presentation"}
					>
						<Checkbox
							checked={isChecked}
							name={item.documentDescription}
							onChange={() => onCheckboxChange(item)}
						></Checkbox>
					</div>
					<span className={"flyout-item-element-text"}>
						{item.documentDescription}
					</span>
				</ListViewItemWrapper>
			</div>
		);
	},
);

FlyoutListItem.displayName = "FlyoutListItem";

export const createFlyoutListItemRenderer = (
	selectedDocument: DefaultDocument,
	checkedItems: Record<string, boolean>,
	onItemClick: (item: DefaultDocument) => void,
	onCheckboxChange: (item: DefaultDocument) => void,
) => {
	const ItemRenderer = (props: ListViewItemProps) => {
		const item = props.dataItem as DefaultDocument;
		const isSelected =
			selectedDocument?.documentFileName.toLowerCase() ===
			item?.documentFileName.toLowerCase();
		const isChecked = checkedItems[item.documentFileName] ?? false;
		return (
			<FlyoutListItem
				isChecked={isChecked}
				isSelected={isSelected}
				item={item}
				onCheckboxChange={onCheckboxChange}
				onItemClick={onItemClick}
			/>
		);
	};

	ItemRenderer.displayName = "FlyoutListItemRenderer";
	return ItemRenderer;
};
